import { useEffect, useRef, useState } from 'react';
import type { ContentLanguage } from '@fame/shared';
import { Eye, RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';

const languages: Record<ContentLanguage, string> = { nl: 'Nederlands (BE)', fr: 'Frans', en: 'Engels' };

export function ContentPreview({
  kind,
  language,
  draft,
}: {
  kind: 'blog' | 'case';
  language: ContentLanguage;
  /** Current save-shaped manual/accepted draft; never pending translation proposals. */
  draft: Record<string, unknown>;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const request = useRef(0);
  const [previewLanguage, setPreviewLanguage] = useState(language);
  const [width, setWidth] = useState<'desktop' | 'mobile'>('desktop');
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(
    () => () => {
      request.current += 1;
    },
    []
  );

  const refresh = async (selectedLanguage = previewLanguage) => {
    const id = ++request.current;
    setLoading(true);
    setError('');
    // Do not leave an older language/draft on screen while a refresh fails or runs.
    setHtml('');
    try {
      const result = await api.post<{ html: string }>('/content-preview', { kind, language: selectedLanguage, draft });
      if (id === request.current) setHtml(result.html);
    } catch (failure) {
      if (id === request.current) setError(failure instanceof Error ? failure.message : 'Voorbeeld laden mislukt.');
    } finally {
      if (id === request.current) setLoading(false);
    }
  };
  const close = () => {
    request.current += 1;
    setHtml('');
    setLoading(false);
    dialog.current?.close();
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => {
          setPreviewLanguage(language);
          dialog.current?.showModal();
          void refresh(language);
        }}
      >
        <Eye className="mr-2 h-4 w-4" />
        Voorbeeld
      </Button>
      <dialog
        ref={dialog}
        onCancel={close}
        onClose={() => {
          request.current += 1;
          setHtml('');
        }}
        aria-labelledby="content-preview-title"
        aria-describedby="content-preview-description"
        className="fixed inset-0 z-50 m-auto h-[94dvh] w-[96vw] max-w-none rounded-lg border bg-background p-0 text-foreground shadow-xl backdrop:bg-black/70"
      >
        <div className="flex h-full min-h-0 flex-col">
          <header className="space-y-3 border-b p-4">
            <div className="flex items-center justify-between gap-4">
              <h2 id="content-preview-title" className="text-lg font-semibold">
                Privévoorbeeld
              </h2>
              <Button type="button" variant="ghost" size="icon" onClick={close} aria-label="Voorbeeld sluiten">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <p id="content-preview-description" className="text-sm text-muted-foreground">
              De websiteweergave van je huidige onopgeslagen tekst. Vernieuwen neemt de laatste handmatige en aanvaarde
              wijzigingen mee. Bronfallback is gemarkeerd; links en formulieren zijn uitgeschakeld.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                Taal
                <select
                  className="rounded border bg-background px-3 py-2"
                  value={previewLanguage}
                  onChange={event => {
                    const next = event.target.value as ContentLanguage;
                    setPreviewLanguage(next);
                    void refresh(next);
                  }}
                >
                  {Object.entries(languages).map(([key, name]) => (
                    <option key={key} value={key}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex gap-1" role="group" aria-label="Voorbeeldbreedte">
                <Button
                  type="button"
                  variant={width === 'desktop' ? 'default' : 'outline'}
                  aria-pressed={width === 'desktop'}
                  onClick={() => setWidth('desktop')}
                >
                  Desktop · 1280 px
                </Button>
                <Button
                  type="button"
                  variant={width === 'mobile' ? 'default' : 'outline'}
                  aria-pressed={width === 'mobile'}
                  onClick={() => setWidth('mobile')}
                >
                  Mobiel · 390 px
                </Button>
              </div>
              <Button type="button" variant="outline" disabled={loading} onClick={() => void refresh()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                {loading ? 'Laden…' : 'Vernieuwen'}
              </Button>
            </div>
            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
            {loading && (
              <p role="status" className="text-sm text-muted-foreground">
                Voorbeeld laden…
              </p>
            )}
          </header>
          <div className="min-h-0 flex-1 overflow-auto bg-muted p-3">
            {html && (
              <iframe
                title={`${languages[previewLanguage]} ${kind === 'blog' ? 'artikel' : 'case'} — privévoorbeeld`}
                sandbox="allow-same-origin"
                referrerPolicy="no-referrer"
                srcDoc={html}
                className="mx-auto block h-full min-h-[480px] border-0 bg-white shadow"
                style={{ width: width === 'desktop' ? 1280 : 390 }}
              />
            )}
          </div>
        </div>
      </dialog>
    </>
  );
}
